import React, { useState } from "react";
import {
    Box,
    Checkbox,
    Collapse,
    IconButton,
    Link,
    List,
    ListItem,
    ListItemText,
    Typography,
    Divider,
    CircularProgress,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { markCertificateRequestProcessed } from "../../http/requestsAPI";

const CertificateRequestsList = ({ requests, markHandler }) => {
    const [showProcessed, setShowProcessed] = useState(false);
    const [processingId, setProcessingId] = useState(null);
    const [expandedComments, setExpandedComments] = useState({});

    const toggleCommentExpand = (id) => {
        setExpandedComments(prev => ({...prev, [id]: !prev[id]}));
    };

    const activeRequests = requests.filter(r => !r.processed);
    const processedRequests = requests.filter(r => r.processed);

    const formatDate = (val) => {
        if (!val) return "";
        try {
            return new Date(val).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' });
        } catch {
            return String(val);
        }
    };

    const handleToggleProcessed = async (request) => {
        if (request.processed) return;
        setProcessingId(request.id);
        try {
            markHandler(request.id);
        } catch (e) {
            alert(e?.response?.data?.error || "Не удалось обновить запрос");
        } finally {
            setProcessingId(null);
        }
    };

    const renderRequest = (request) => (
        <ListItem
            key={request.id}
            dense
            sx={{
                alignItems: "flex-start",
                opacity: request.processed ? 0.6 : 1,
                pl: 0,
            }}
        >
            <Checkbox
                checked={request.processed}
                disabled={request.processed || processingId === request.id}
                onChange={() => handleToggleProcessed(request)}
                sx={{ pt: 0 }}
            />
            {processingId === request.id ? (
                <CircularProgress size={20} sx={{ mt: 1, mr: 1 }} />
            ) : null}
            <ListItemText
                primary={
                    <Box>
                        <Typography variant="body2" fontWeight={request.processed ? 400 : 600}>
                            Запрос сертификата
                        </Typography>
                        {request.description && (() => {
                            const content = request.description.trim();
                            const isExpanded = expandedComments[request.id];
                            const needsTruncate = content.length > 150;
                            return (
                                <Box>
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, whiteSpace: isExpanded ? 'pre-line' : 'normal' }}>
                                        {isExpanded || !needsTruncate
                                            ? content
                                            : content.substr(0, 150) + '...'
                                        }
                                    </Typography>
                                    {needsTruncate && (
                                        <Link
                                            component="button"
                                            variant="caption"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                toggleCommentExpand(request.id);
                                            }}
                                            sx={{ ml: 0.5, fontSize: '0.7rem' }}
                                        >
                                            {isExpanded ? 'скрыть' : 'показать всё'}
                                        </Link>
                                    )}
                                </Box>
                            );
                        })()}
                    </Box>
                }
                secondary={
                    <Typography variant="caption" color="text.secondary">
                        {formatDate(request.createdAt)}
                        {request.processed && request.admin && ` • Обработано: ${request.admin.internalName || request.admin.name}`}
                    </Typography>
                }
            />
        </ListItem>
    );

    return (
        <Box sx={{ mt: 1 }}>
            {activeRequests.length === 0 && processedRequests.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1 }}>
                    Нет запросов
                </Typography>
            ) : (
                <>
                    <List dense sx={{ pt: 0 }}>
                        {activeRequests.map(renderRequest)}
                    </List>

                    {processedRequests.length > 0 && (
                        <>
                            <Divider sx={{ my: 1 }} />
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    cursor: "pointer",
                                    pl: 1,
                                }}
                                onClick={() => setShowProcessed(!showProcessed)}
                            >
                                <IconButton size="small">
                                    {showProcessed ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                </IconButton>
                                <Typography variant="body2" color="text.secondary">
                                    Обработанные ({processedRequests.length})
                                </Typography>
                            </Box>
                            <Collapse in={showProcessed}>
                                <List dense sx={{ pt: 0 }}>
                                    {processedRequests.map(renderRequest)}
                                </List>
                            </Collapse>
                        </>
                    )}
                </>
            )}
        </Box>
    );
};

export default CertificateRequestsList;
